import type { StockSeries } from '@jums-dashboard/shared';
import { useState } from 'react';

import Widget, { type WidgetStatus } from '@/components/Widget/Widget';
import { useDashboardBreakpoint, type DashboardBreakpoint } from '@/features/dashboard/hooks/useDashboardBreakpoint';
import { cn } from '@/lib/cn';

import { resolveAsset, type ResolvedAsset } from './config';
import StocksDetailModal from './StocksDetailModal';
import StocksSparkline from './StocksSparkline';
import { CHANGE_COLOR_CLASS, changeSign, deriveAsset, formatPrice } from './stocksDerive';
import useStocks from './useStocks';
import useStocksWidgetConfig from './useStocksWidgetConfig';

function stocksCapacity(bp: DashboardBreakpoint): number {
  return bp === 'wide' ? 7 : bp === 'tablet' ? 5 : 4;
}

function fmtPct(pct: number | null): string {
  if (pct === null || !Number.isFinite(pct)) return '—';
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
}

export default function StocksWidget() {
  const config = useStocksWidgetConfig();
  const query = useStocks(config);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const capacity = stocksCapacity(useDashboardBreakpoint());

  const resolveById = (id: string): ResolvedAsset | null => resolveAsset(config.watchlist.find((entry) => entry.id === id) ?? { id });
  const orderedIds = config.visible.length > 0 ? config.visible : config.watchlist.map((entry) => entry.id);
  const orderedAssets = orderedIds.map(resolveById).filter((asset): asset is ResolvedAsset => asset != null);
  const visibleAssets = orderedAssets.slice(0, capacity);
  const hiddenAssets = orderedAssets.slice(capacity);
  const allAssets = config.watchlist.map((entry) => resolveAsset(entry)).filter((asset): asset is ResolvedAsset => asset != null);

  const seriesById = query.data ?? {};
  const status: WidgetStatus = query.isLoading ? 'loading' : query.isError ? 'error' : visibleAssets.length === 0 ? 'empty' : 'loaded';

  return (
    <>
      <Widget
        title="Stocks"
        status={status}
        errorMessage={query.error instanceof Error ? query.error.message : 'Failed to load market data'}
        emptyMessage="No assets selected. Open settings to choose what to show."
        loadingMessage="Loading market data…"
        onOpenDetail={visibleAssets.length > 0 ? () => setSelectedId(visibleAssets[0]!.id) : undefined}
      >
        <div className="flex h-full flex-col">
          <ul className="flex flex-1 flex-col justify-evenly">
            {visibleAssets.map((asset) => (
              <li key={asset.id}>
                <StockRow asset={asset} series={seriesById[asset.id]} onOpen={setSelectedId} />
              </li>
            ))}
          </ul>
          {hiddenAssets.length > 0 && (
            <button
              type="button"
              onClick={() => setSelectedId(visibleAssets[0]?.id ?? hiddenAssets[0]!.id)}
              className="mt-2 truncate border-t border-(--border-subtle) pt-2 text-left text-[11px] text-(--text-muted) transition-colors hover:text-(--text-tertiary)"
            >
              +{hiddenAssets.length} more · {hiddenAssets.map((a) => a.label).join(', ')} — tap for all ›
            </button>
          )}
        </div>
      </Widget>

      <StocksDetailModal
        open={selectedId != null}
        onClose={() => setSelectedId(null)}
        assets={allAssets}
        seriesById={seriesById}
        selectedId={selectedId}
        onSelect={setSelectedId}
        lastUpdated={query.dataUpdatedAt ? new Date(query.dataUpdatedAt) : null}
        onRefresh={() => query.refetch()}
      />
    </>
  );
}

function StockRow({ asset, series, onOpen }: { asset: ResolvedAsset; series: StockSeries | undefined; onOpen: (id: string) => void }) {
  const derived = deriveAsset(series);
  const currency = asset.currency || series?.currency || '';
  const sign = changeSign(derived.dayChangeAbs);
  const hasError = series?.error != null;

  return (
    <button
      type="button"
      onClick={() => onOpen(asset.id)}
      aria-label={`Open ${asset.label} details`}
      className="grid w-full grid-cols-[1fr_74px_auto] items-center gap-x-3 py-1 text-left focus-visible:outline-none"
    >
      <span className="min-w-0">
        <span className="block truncate text-sm text-(--text-secondary)">{asset.label}</span>
        {!hasError && (
          <span className="font-display block text-[17px] leading-tight font-semibold text-(--text-primary) tabular-nums">
            {formatPrice(derived.current, currency)}
          </span>
        )}
      </span>
      {hasError ? (
        <span className="col-span-2 text-right text-xs text-(--accent-danger)">Unavailable</span>
      ) : (
        <>
          <StocksSparkline points={series?.points ?? []} sign={sign} className="h-6 w-full" />
          <span className={cn('text-right text-[13px] font-medium tabular-nums', CHANGE_COLOR_CLASS[sign])}>
            {fmtPct(derived.dayChangePct)}
          </span>
        </>
      )}
    </button>
  );
}
